import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import CompanyPicker from '../CompanyPicker';
import ContactMultiSelect from './ContactMultiSelect';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionsContext';
import { errorText } from '../../lib/apiErrors';
import { dealStages, dealStageMeta, newDealForm, isUnknownStage } from '../../lib/dealStages';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const NO_CONTACTS = [];
const READY = { status: 'ready', baseline: NO_CONTACTS, restricted: 0 };

// Computed on read (`GET /deals?include=card_context`) or owned by the contact-link endpoints: never sent back in a PUT.
const NOT_SENT = ['contact_preview', 'deal_owner_name', 'linked_contact_count', 'stage_label', 'contact_link', 'contact_ids', 'stakeholders'];

const personOf = (row) => ({
    contact_id: row.contact_id,
    first_name: row.contact?.first_name || '',
    last_name: row.contact?.last_name || '',
    email: row.contact?.email || '',
});

/**
 * Create or edit a deal. `deal` is a stored deal (edit) or a stage-only prefill from a column's "Add deal";
 * `initial` prefills a NEW deal: `{account_id, account_name, contacts: [{contact_id, first_name, last_name, email}]}`.
 * `onSave(saved, {linksChanged})` runs after the deal is stored; `linksChanged` says a contact link was written, so
 * a list that shows linked contacts should re-read. `onStageRejected` runs when the server refused the stage.
 *
 * Contacts (#906). A new deal sends them as `contact_ids`. An edit never does: after the PUT it adds and removes
 * only the difference from what `GET /deals/{id}/stakeholders` returned on open, through the contact-link endpoints.
 */
export default function DealModal({ open, onClose, deal, initial, onSave, definition, onStageRejected }) {
    const { getAuthHeader } = useAuth();
    const { hasEffectivePermission } = usePermissions();
    const stages = dealStages(definition);
    // Only a stored deal is an edit; the column "Add deal" button passes a stage-only prefill.
    const isEdit = Boolean(deal?.deal_id);
    const dealId = isEdit ? deal.deal_id : null;
    // callers pass `initial` inline: compare it by content so a re-render never resets the form
    const initialKey = JSON.stringify(initial ?? null);
    const seed = useMemo(() => JSON.parse(initialKey), [initialKey]);
    const [form, setForm] = useState(() => newDealForm(definition));
    const [loading, setLoading] = useState(false);
    const [contacts, setContacts] = useState(NO_CONTACTS);
    const [links, setLinks] = useState(READY);
    // a token refresh replaces `getAuthHeader`; it must not reload the contacts and drop the user's unsaved picks
    const authHeader = useRef(getAuthHeader);
    useEffect(() => { authHeader.current = getAuthHeader; }, [getAuthHeader]);

    useEffect(() => {
        if (deal?.deal_id) setForm({ ...deal, value: deal.value || '' });
        else setForm({ ...newDealForm(definition, deal?.stage), account_id: seed?.account_id || '', account_name: seed?.account_name || '' });
    }, [deal, open, definition, seed]);

    useEffect(() => {
        if (!open) { setContacts(NO_CONTACTS); setLinks(READY); return undefined; }
        if (!dealId) { setContacts(seed?.contacts || NO_CONTACTS); setLinks(READY); return undefined; }
        let cancelled = false;
        setContacts(NO_CONTACTS);
        setLinks({ status: 'loading', baseline: NO_CONTACTS, restricted: 0 });
        axios.get(`${API}/deals/${dealId}/stakeholders`, { headers: authHeader.current() })
            .then(res => {
                if (cancelled) return;
                const rows = Array.isArray(res.data) ? res.data : [];
                const people = rows.filter(r => r?.contact_id && r.contact && !r.restricted).map(personOf);
                setContacts(people);
                setLinks({ status: 'ready', baseline: people.map(p => p.contact_id), restricted: rows.filter(r => r?.restricted).length });
            })
            .catch(err => { if (!cancelled) setLinks({ status: err?.response?.status === 403 ? 'denied' : 'error', baseline: NO_CONTACTS, restricted: 0 }); });
        return () => { cancelled = true; };
    }, [open, dealId, seed]);

    // The pages that open this form for a NEW deal gate on deals:create (the server's gate on POST /deals); changing a stored deal's links needs deals:update, as the server does.
    const canEditLinks = !isEdit || hasEffectivePermission('deals:update');
    const linksNotice = !isEdit ? null
        : links.status === 'loading' ? 'Loading linked contacts…'
            : links.status === 'denied' ? "You don't have access to this deal's linked contacts."
                : links.status === 'error' ? "This deal's linked contacts could not be loaded. Close and reopen the deal to try again."
                    : !canEditLinks ? 'You need permission to update deals to change its contacts.' : null;

    // Add and remove the difference from the contacts read on open. Never throws: the deal is already saved.
    const syncLinks = async (id) => {
        if (links.status !== 'ready' || !canEditLinks) return false;
        const headers = getAuthHeader();
        const wanted = new Set(contacts.map(c => c.contact_id));
        const had = new Set(links.baseline);
        const writes = [
            ...contacts.filter(c => !had.has(c.contact_id))
                .map(c => () => axios.post(`${API}/deals/${id}/stakeholders`, { contact_id: c.contact_id }, { headers })),
            ...links.baseline.filter(cid => !wanted.has(cid))
                .map(cid => () => axios.delete(`${API}/deals/${id}/contacts/${cid}`, { headers })),
        ];
        if (writes.length === 0) return false;
        const results = await Promise.allSettled(writes.map(async (write) => write()));
        const failed = results.filter(r => r.status === 'rejected');
        if (failed.length) {
            toast.error(`Deal saved, but ${failed.length === 1 ? '1 contact change' : `${failed.length} contact changes`} could not be applied. ${errorText(failed[0].reason, 'Reopen the deal to check its contacts.')}`);
        }
        return failed.length < writes.length;
    };

    const handleSave = async () => {
        if (!form.title) return toast.error('Deal title required');
        setLoading(true);
        try {
            const payload = { ...form, value: parseFloat(form.value) || 0 };
            NOT_SENT.forEach(k => delete payload[k]);
            let saved;
            let linksChanged = false;
            if (isEdit) {
                const res = await axios.put(`${API}/deals/${deal.deal_id}`, payload, { headers: getAuthHeader() });
                saved = res.data;
                linksChanged = await syncLinks(deal.deal_id);
            } else {
                const res = await axios.post(`${API}/deals`, { ...payload, contact_ids: contacts.map(c => c.contact_id) }, { headers: getAuthHeader() });
                saved = res.data;
                linksChanged = contacts.length > 0;
            }
            toast.success(isEdit ? 'Deal updated' : 'Deal created');
            onSave(saved, { linksChanged });
            onClose();
        } catch (err) {
            toast.error(errorText(err, 'Failed to save deal'));
            if (isUnknownStage(err)) onStageRejected?.();
        } finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{isEdit ? 'Edit Deal' : 'New Deal'}</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 mt-2">
                    <div className="col-span-2">
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Deal Title *</label>
                        <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Enterprise License Deal"
                            data-testid="deal-title-input"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-400" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Company</label>
                        <CompanyPicker value={form.account_id || ''} selectedLabel={form.account_name} placeholder="Select company…"
                            onChange={(id, name) => setForm(f => ({ ...f, account_id: id, account_name: name }))}
                            apiBase={API} getAuthHeader={getAuthHeader} testId="deal-account-select" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Stage</label>
                        <select value={form.stage} onChange={e => setForm({ ...form, stage: e.target.value, probability: dealStageMeta(definition, e.target.value).probability ?? form.probability })} data-testid="deal-stage-select"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                            {stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                            {/* a stored stage outside the definition stays selectable, so saving does not silently change it */}
                            {form.stage && !stages.some(s => s.key === form.stage) && <option value={form.stage}>{`(current) ${form.stage}`}</option>}
                        </select>
                    </div>
                    <div className="col-span-2" data-testid="deal-contacts-field">
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Contacts</label>
                        <ContactMultiSelect value={contacts} onChange={setContacts} accountId={form.account_id || ''} accountName={form.account_name || ''}
                            disabled={linksNotice !== null} disabledReason={linksNotice} />
                        {links.restricted > 0 && (
                            <p className="mt-1 text-xs text-stone-400" data-testid="deal-contacts-restricted">
                                {`+${links.restricted} contact${links.restricted === 1 ? '' : 's'} you can't see`}
                            </p>
                        )}
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Deal Value ($)</label>
                        <input type="number" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder="50000"
                            data-testid="deal-value-input"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-400" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Close Date</label>
                        <input type="date" value={form.expected_close_date || ''} onChange={e => setForm({ ...form, expected_close_date: e.target.value })}
                            data-testid="deal-close-date-input"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Source</label>
                        <select value={form.source || ''} onChange={e => setForm({ ...form, source: e.target.value })} data-testid="deal-source-select"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                            <option value="">Select...</option>
                            {['inbound', 'outbound', 'referral', 'partner'].map(s => <option key={s} className="capitalize">{s}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Probability (%)</label>
                        <input type="number" min="0" max="100" value={form.probability} onChange={e => setForm({ ...form, probability: parseInt(e.target.value) })}
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                    </div>
                    <div className="col-span-2">
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Description</label>
                        <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-4">
                    <button onClick={onClose} className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                    <button onClick={handleSave} disabled={loading} data-testid="deal-save-btn"
                        className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50">
                        {loading ? 'Saving...' : 'Save Deal'}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
